// Core Web Vitals Performance Monitoring
// Reports metrics to Sentry and console for analysis
import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';
import type { Metric } from 'web-vitals';
import * as Sentry from '@sentry/react';

/**
 * Core Web Vitals Thresholds (Google's recommendations)
 *
 * LCP (Largest Contentful Paint): < 2.5s good, > 4s poor
 * CLS (Cumulative Layout Shift): < 0.1 good, > 0.25 poor
 * FCP (First Contentful Paint): < 1.8s good, > 3s poor
 * TTFB (Time to First Byte): < 800ms good, > 1800ms poor
 * INP (Interaction to Next Paint): < 200ms good, > 500ms poor
 */

type MetricRating = 'good' | 'needs-improvement' | 'poor';

/**
 * Get rating for a metric based on thresholds
 */
const getRating = (name: string, value: number): MetricRating => {
  const thresholds: Record<string, [number, number]> = {
    CLS: [0.1, 0.25],
    FCP: [1800, 3000],
    LCP: [2500, 4000],
    TTFB: [800, 1800],
    INP: [200, 500],
  };

  const [good, poor] = thresholds[name] || [0, 0];

  if (value <= good) return 'good';
  if (value <= poor) return 'needs-improvement';
  return 'poor';
};

/**
 * Report metric to Sentry
 */
const reportToSentry = (metric: Metric) => {
  const { name, value, rating, id } = metric;

  // Report as Sentry metric
  Sentry.metrics.distribution(`web_vital.${name.toLowerCase()}`, value, {
    unit: name === 'CLS' ? '' : 'millisecond',
    attributes: {
      rating: rating || getRating(name, value),
      metric_id: id,
    },
  });

  // Also capture as breadcrumb for context in error reports
  Sentry.addBreadcrumb({
    category: 'web-vitals',
    message: `${name}: ${value.toFixed(name === 'CLS' ? 3 : 0)}${name === 'CLS' ? '' : 'ms'} (${rating || getRating(name, value)})`,
    level: rating === 'poor' ? 'warning' : 'info',
    data: {
      name,
      value,
      rating: rating || getRating(name, value),
      id,
    },
  });
};

/**
 * Log metric to console in development
 */
const logToConsole = (metric: Metric) => {
  const { name, value, rating } = metric;
  const ratingEmoji =
    rating === 'good' ? '✅' : rating === 'needs-improvement' ? '⚠️' : '❌';

  console.log(
    `${ratingEmoji} ${name}: ${value.toFixed(name === 'CLS' ? 3 : 0)}${name === 'CLS' ? '' : 'ms'} (${rating})`
  );
};

/**
 * Handle metric reporting
 */
const handleMetric = (metric: Metric) => {
  // Always report to Sentry if initialized
  reportToSentry(metric);

  // Log to console in development
  if (import.meta.env.DEV) {
    logToConsole(metric);
  }
};

/**
 * Initialize Core Web Vitals monitoring
 * Call this once at app startup
 */
export const initWebVitals = () => {
  // Largest Contentful Paint - loading performance
  onLCP(handleMetric);

  // Cumulative Layout Shift - visual stability
  onCLS(handleMetric);

  // First Contentful Paint - initial render
  onFCP(handleMetric);

  // Time to First Byte - server response
  onTTFB(handleMetric);

  // Interaction to Next Paint - responsiveness
  onINP(handleMetric);

  if (import.meta.env.DEV) {
    console.log('📊 Core Web Vitals monitoring initialized');
  }
};

/**
 * Manually report a custom performance metric
 */
export const reportCustomMetric = (
  name: string,
  value: number,
  unit: string = 'millisecond'
) => {
  Sentry.metrics.distribution(`custom.${name}`, value, {
    unit,
  });
};
